import {useQueryClient} from "@tanstack/react-query";
import {
  AlertCircleIcon,
  Calendar,
  Car,
  Clock,
  Loader2,
  MapPin,
  Play,
} from "lucide-react";
import {useState} from "react";
import {useNavigate} from "react-router-dom";

import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {shouldPromptPatrolStartConditionCheck} from "@/src/modules/company-vehicles/lib/patrol-vehicle-condition";
import {formatPatrolVehicleDisplay} from "@/src/modules/company-vehicles/lib/format-patrol-vehicle-display";
import {dispatchCenterRequiresShift, isDispatchOperatorRole} from "@/src/modules/core/lib/dispatch-center-policy";
import {listShiftCompanyVehicles} from "@/src/modules/company-vehicles/lib/shift-company-vehicles";
import {BadgeOverflowList, CoordinatesRequiredGate, LocationRequiredDialog} from "@/src/modules/core/components";
import {useInvalidate, useToast} from "@/src/modules/core/hooks";
import {useCoordinatesGate} from "@/src/modules/core/hooks/use-coordinates-gate";
import {useLocationGatedOpen} from "@/src/modules/core/hooks/use-location-gated-open";
import {resolveShiftGpsForSubmit} from "@/src/modules/core/lib/resolve-shift-gps-coordinates";
import {formatDate, formatTime, moment} from "@/src/modules/core/lib";
import {useStoredMe} from "@/src/modules/core/store/me";
import {StatusBadge} from "@/src/modules/shifts/components";
import {ShiftPropertyDisplayBadges} from "@/src/modules/shifts/components/shift-property-display-badges";
import {Shift, ShiftType} from "@/src/modules/shifts/types";

import useNotificationPermission from "@/src/modules/notifications/hooks/use-notification-permissions";

import {useSelfWorkEligibility} from "@/src/modules/employee-hr/hooks/use-work-eligibility";

import {syncAfterShiftStartSuccess, buildActiveShiftFromStart} from "../../lib/shift-start-cache";
import {
  getSecurityLicenseBlockDisplay,
  isSecurityLicenseRequiredError,
  type SecurityLicenseBlockDisplay,
} from "../../lib/shift-start-license-errors";
import {useShiftStart} from "../../hooks";
import {officerService} from "../../services";
import {ShiftLicenseBlockDialog} from "./shift-license-block-dialog";
import {StartShiftAcknowledgmentModal} from "./start-shift-acknowledgment-modal";
import {useTranslation} from "react-i18next";

interface NextShift {
  shift: Shift;
}

export function NextShift({shift}: NextShift) {
  const {t} = useTranslation("officer");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const invalidateMe = useInvalidate("me");
  const invalidateShifts = useInvalidate("shifts");
  const {mutateAsync, isPending} = useShiftStart();
  const {requestPermission, status: notificationPermissionStatus} = useNotificationPermission();
  const {submitBlockedByLocation} = useCoordinatesGate(true);
  const {requestOpen, locationDialogProps, isCheckingLocation} = useLocationGatedOpen();
  const me = useStoredMe();
  const {eligibility} = useSelfWorkEligibility();
  const [showAckModal, setShowAckModal] = useState(false);
  const [licenseBlockOpen, setLicenseBlockOpen] = useState(false);
  const [licenseBlockDisplay, setLicenseBlockDisplay] =
    useState<SecurityLicenseBlockDisplay | null>(null);
  const showEligibilityWarning = eligibility != null && !eligibility.can_start_shifts;
  const now = moment();
  const startDateTime = moment(shift.from_datetime);
  const canStartShiftNow = officerService.canStartShiftNow(shift, me, now);
  const isBeforeShiftStart = !canStartShiftNow;
  const isSeniorSupervisorEarlyWindow = officerService.isSeniorSupervisorEarlyStartWindow(
    shift,
    me,
    now,
  );
  const isPastScheduledStart = now.isAfter(startDateTime);
  const isPatrolShift = shift.type === ShiftType.PATROL;
  const earlyStartEndNotice = isSeniorSupervisorEarlyWindow
    ? officerService.getEarlyStartEndAdjustmentNotice(shift, now, true)
    : null;
  const scheduleSubtitle = isBeforeShiftStart
    ? `Starting ${startDateTime.fromNow()}`
    : isSeniorSupervisorEarlyWindow
      ? `You can start now — scheduled start ${startDateTime.fromNow()}.`
      : isPastScheduledStart
        ? "Past your scheduled start time — you can still start when ready."
        : "Your scheduled start time is now.";
  const conditionsText = me?.company?.officer_teams_and_conditions?.trim() ?? "";
  const patrolVehicles = isPatrolShift ? listShiftCompanyVehicles(shift) : [];
  const patrolVehiclesNewestFirst = [...patrolVehicles].reverse();

  const handleOpenAckModal = () => {
    void requestOpen(() => setShowAckModal(true));
  };

  const handleAcknowledge = async () => {
    try {
      const gps = await resolveShiftGpsForSubmit(shift, "shift_start");
      if (!gps) {
        return;
      }
      const today = moment();
      const data = {
        shift_id: shift.shift_id,
        gps_coordinates: gps.gps_coordinates,
        gps_source: gps.gps_source,
        date: today.format("YYYY-MM-DD"),
        time: today.format("HH:mm:ss"),
        officer_teams_and_conditions_acknowledged: 1,
      };
      const started = await mutateAsync(data);
      await syncAfterShiftStartSuccess(queryClient, shift, started, invalidateShifts);
      const startMoment = moment();
      const earlyEndNotice = officerService.getEarlyStartEndAdjustmentNotice(shift, startMoment);
      toast.success(t("toast.shift_started"), {
        ...(earlyEndNotice ? {description: earlyEndNotice} : {}),
      });
      if (isDispatchOperatorRole(me) && dispatchCenterRequiresShift(me)) {
        const startedShift = buildActiveShiftFromStart(shift, started);
        if (!shouldPromptPatrolStartConditionCheck(startedShift)) {
          navigate("/dispatchdesk", {replace: true});
        }
      }
      if (notificationPermissionStatus === "default") {
        void requestPermission();
      }
      invalidateMe();
      setShowAckModal(false);
    } catch (error) {
      if (isSecurityLicenseRequiredError(error)) {
        setLicenseBlockDisplay(getSecurityLicenseBlockDisplay(error));
        setLicenseBlockOpen(true);
      }
      setShowAckModal(false);
    }
  };

  return (
    <Card className="flex h-full min-h-0 flex-1 flex-col">
      <CardHeader className="shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl">Your Next Shift</CardTitle>
            <CardDescription className="flex flex-col gap-0.5">
              <span>{scheduleSubtitle}</span>
              {earlyStartEndNotice ? (
                <span className="text-xs text-amber-700 dark:text-amber-500">
                  {earlyStartEndNotice}
                </span>
              ) : null}
            </CardDescription>
          </div>
          <Badge variant="secondary" className="text-sm px-3 py-1">
            {shift.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {/* Officer Info */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{shift.user!.name}</h3>
            <StatusBadge shift={shift} />
          </div>
        </div>

        {/* Shift details — one grid, same icon + stack pattern */}
        <div className="grid min-w-0 gap-6 sm:grid-cols-2">
          <div className="flex items-start gap-3">
            <Calendar className="size-5 shrink-0 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-medium">Date</p>
              <p className="text-sm text-muted-foreground">{formatDate(shift.from_datetime)}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="size-5 shrink-0 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-medium">Time</p>
              <p className="text-sm text-muted-foreground">
                {formatTime(shift.from_datetime)} - {formatTime(shift.to_datetime)}
              </p>
            </div>
          </div>
          <div className="flex min-w-0 items-start gap-3 sm:col-span-2">
            <MapPin className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Properties</p>
              {shift.properties.length > 0 ? (
                <div className="mt-1">
                  <ShiftPropertyDisplayBadges
                    properties={shift.properties}
                    className="w-full min-w-0"
                  />
                </div>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">—</p>
              )}
            </div>
          </div>
          {isPatrolShift ? (
            <div className="flex min-w-0 items-start gap-3 sm:col-span-2">
              <Car className="size-5 shrink-0 text-muted-foreground mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">Patrol vehicle</p>
                {patrolVehicles.length > 0 ? (
                  <div className="mt-1">
                    <BadgeOverflowList
                      align="start"
                      className="w-full min-w-0"
                      items={patrolVehiclesNewestFirst}
                      maxVisible={1}
                      getKey={(v, i) => `patrol-vehicle-${v.id}-${i}`}
                      popoverTitle="All company vehicles"
                      getLabel={(v) => formatPatrolVehicleDisplay(v)}
                      overflowPairLayout="flex"
                      clampVisibleLabel={false}
                      visibleBadgeClassName="max-w-full whitespace-normal break-words py-1 leading-snug text-left overflow-visible"
                      popoverItemClassName="whitespace-normal break-words"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">—</p>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex w-full flex-col gap-2 pt-2">
          {showEligibilityWarning ? (
            <Alert variant="destructive" className="w-full">
              <AlertCircleIcon />
              <AlertTitle>You may not be able to start this shift</AlertTitle>
              <AlertDescription className="flex flex-col gap-2">
                {eligibility?.blocked_reasons.length ? (
                  <ul className="flex flex-col gap-1">
                    {eligibility.blocked_reasons.map((reason) => (
                      <li key={`${reason.license_type}-${reason.status}`}>
                        {reason.license_type_label}: {reason.status_label}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span>Your security license requirements are not met.</span>
                )}
                {eligibility?.open_authorization?.reason ? (
                  <span>Restriction: {eligibility.open_authorization.reason}</span>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
          {isBeforeShiftStart ? (
            <Alert variant="info" className="w-full">
              <AlertCircleIcon />
              <AlertTitle>The shift starts {startDateTime.fromNow()}.</AlertTitle>
            </Alert>
          ) : (
            <>
              {submitBlockedByLocation ? <CoordinatesRequiredGate /> : null}
              <Button
                size="lg"
                className="flex w-full items-center justify-center gap-2"
                disabled={submitBlockedByLocation || isPending || isCheckingLocation}
                onClick={handleOpenAckModal}
              >
              <Play className="size-4" />
              Start shift
              {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            </Button>
            </>
          )}
        </div>
      </CardContent>

      <StartShiftAcknowledgmentModal
        open={showAckModal}
        content={conditionsText}
        onAcknowledge={handleAcknowledge}
        onRequestClose={() => setShowAckModal(false)}
        isPending={isPending}
      />
      <LocationRequiredDialog {...locationDialogProps} />
      <ShiftLicenseBlockDialog
        open={licenseBlockOpen}
        display={licenseBlockDisplay}
        onOpenChange={setLicenseBlockOpen}
      />
    </Card>
  );
}
